import { useEffect, useRef, useState } from "react";
import { Box, Typography } from "@mui/material";
import { viewportCappedHeightSx } from "config";
import {
  useLazyGetPdfJobLogsQuery,
  type PdfJobLogEvent,
} from "features/pdfTools/pdfTools";

const POLL_MS = 3000;

/** The task page's log window: its designed height, shrinking on a short
 * laptop screen instead of pushing everything under it off the page. */
const LOG_BOX_SIZING = viewportCappedHeightSx(180, 460, 430);
const MAX_LINES = 4000;

const ERROR_RX = /(?:\b|^)(ERROR|CRITICAL|Traceback|Exception)\b/i;

function formatTs(ms: number): string {
  const d = new Date(ms);
  return d.toLocaleTimeString();
}

function LogLine({ event }: { event: PdfJobLogEvent }) {
  const text = event.message ?? "";
  const isError = ERROR_RX.test(text);
  return (
    <Box
      sx={{
        display: "flex",
        gap: 1,
        py: 0.15,
        px: 0.5,
        borderRadius: 0.5,
        "&:hover": { bgcolor: "action.hover" },
        fontFamily: "monospace",
        fontSize: "0.78rem",
        lineHeight: 1.45,
        whiteSpace: "pre-wrap",
        wordBreak: "break-word",
        color: isError ? "error.main" : "text.primary",
      }}
    >
      {event.timestamp ? (
        <Box
          component="span"
          sx={{ flexShrink: 0, color: "text.secondary", fontSize: "0.74rem" }}
        >
          {formatTs(event.timestamp)}
        </Box>
      ) : null}
      <Box component="span" sx={{ flex: 1 }}>
        {text}
      </Box>
    </Box>
  );
}

interface JobLogsPanelProps {
  jobId: string;
  /** Keep polling while the job is running. */
  live: boolean;
}

export default function JobLogsPanel({ jobId, live }: JobLogsPanelProps) {
  const [fetchLogs] = useLazyGetPdfJobLogsQuery();
  const [events, setEvents] = useState<PdfJobLogEvent[]>([]);
  const [loaded, setLoaded] = useState(false);
  const nextTokenRef = useRef<string | null>(null);
  const boxRef = useRef<HTMLDivElement | null>(null);

  // A new job starts from an empty box; a job that stops running keeps its
  // lines (the polling below just takes one last look).
  useEffect(() => {
    setEvents([]);
    setLoaded(false);
    nextTokenRef.current = null;
  }, [jobId]);

  useEffect(() => {
    let stopped = false;
    let inFlight = false;

    const tick = async () => {
      if (stopped || inFlight) return;
      inFlight = true;
      try {
        const res = await fetchLogs({
          jobId,
          next_token: nextTokenRef.current,
        }).unwrap();
        if (stopped) return;
        setLoaded(true);
        if (res.source === "file") {
          // The mirrored log file is re-read whole each time.
          setEvents(res.events.slice(-MAX_LINES));
          nextTokenRef.current = null;
        } else if (res.events.length > 0 || nextTokenRef.current === null) {
          nextTokenRef.current = res.next_token;
          if (res.events.length > 0) {
            setEvents((prev) => [...prev, ...res.events].slice(-MAX_LINES));
          }
        } else {
          nextTokenRef.current = res.next_token;
        }
      } catch {
        if (!stopped) setLoaded(true);
      } finally {
        inFlight = false;
      }
    };

    void tick();
    if (!live) {
      return () => {
        stopped = true;
      };
    }
    const interval = setInterval(() => void tick(), POLL_MS);
    return () => {
      stopped = true;
      clearInterval(interval);
    };
  }, [jobId, live, fetchLogs]);

  useEffect(() => {
    const box = boxRef.current;
    if (!box) return;
    const nearBottom =
      box.scrollHeight - box.scrollTop - box.clientHeight < 120;
    if (nearBottom) box.scrollTop = box.scrollHeight;
  }, [events]);

  return (
    <Box
      ref={boxRef}
      data-inner-scroll="logs"
      sx={{
        fontFamily: "monospace",
        fontSize: "0.78rem",
        m: 0,
        p: 1.5,
        bgcolor: "background.paper",
        color: "text.primary",
        border: 1,
        borderColor: "divider",
        borderRadius: 1,
        overflow: "auto",
        ...LOG_BOX_SIZING,
      }}
    >
      {events.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
          {loaded
            ? live
              ? "Waiting for log lines..."
              : "No logs are available for this job."
            : "Loading logs..."}
        </Typography>
      ) : (
        events.map((event, idx) => <LogLine key={idx} event={event} />)
      )}
    </Box>
  );
}
